// ============================================================================
// BESPOKIAN Health - How It Works Page
// Step-by-step guide with luxury aesthetic
// ============================================================================

import { Navigation } from '@/components/layout/Navigation';
import { Footer } from '@/components/layout/Footer';
import { Link } from '@tanstack/react-router';
import { ArrowRight, Shield, Clock, Award, Phone, MessageCircle } from 'lucide-react';

const goldenThreadSteps = [
  {
    title: 'Direct Intake',
    description: 'Your physician transmits your prescription directly to our secure portal. We initiate the intake process immediately—seamlessly bridging the gap between diagnosis and treatment.',
  },
  {
    title: 'Intelligent Price Analysis',
    description: 'We automatically audit your prescription against both insurance copays and our direct-to-consumer pricing. You are presented with the lowest possible cost, instantly. No obscure formularies; just the best price.',
  },
  {
    title: 'Clinical Validation',
    description: 'Safety is paramount. Every order undergoes a rigorous clinical review by a licensed pharmacist to evaluate drug interactions, dosage accuracy, and therapeutic suitability before fulfillment begins.',
  },
  {
    title: 'Precision Dispensing',
    description: 'Your medication is prepared in our climate-controlled, NABP-compliant facility. We utilize advanced verification technology to ensure the highest standards of quality and freshness.',
  },
  {
    title: 'Expedited Logistics',
    description: 'We respect your time. Orders are dispatched via priority shipping (typically 2-3 days) with end-to-end tracking. Direct-to-doorstep service means you never wait in line again.',
  },
  {
    title: 'Continuous Clinical Care',
    description: 'Our relationship doesn\'t end at delivery. Our clinical team remains available to answer questions, monitor adherence, and manage renewals, acting as an extension of your healthcare team.',
  },
];

const trustBadges = [
  { icon: Shield, label: 'Licensing In Progress', description: 'Currently in licensing phase for all 50 states' },
  { icon: Award, label: 'HIPAA Compliance In Progress', description: 'Rigorous protocols to protect your health data' },
  { icon: Clock, label: 'Pharmacist Support', description: 'Licensed pharmacists available to assist' },
  { icon: Phone, label: 'Concierge Care', description: 'Dedicated clinical support line' },
];

export function HowItWorksPage() {
  const scrollToSteps = () => {
    const stepsSection = document.getElementById('steps-section');
    if (stepsSection) {
      stepsSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Navigation />

      <main className="flex-1">
        {/* Hero Section with Chemistry Lab Background */}
        <section
          className="relative h-screen flex items-center justify-center overflow-hidden"
          style={{
            backgroundImage: 'linear-gradient(rgba(0, 21, 58, 0.55), rgba(0, 21, 58, 0.9)), url("/img/heroes/health-how-it-works-hero.webp")',
            backgroundSize: 'cover',
            backgroundPosition: 'center center'
          }}
        >
          <div className="relative z-10 w-full max-w-4xl mx-auto px-6 text-center">
            <div className="hidden md:block w-24 h-0.5 bg-white mx-auto mb-8" />
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif text-white mb-6 md:mb-8 leading-tight tracking-tight">
              Simple & Transparent
            </h1>
            <div className="hidden md:block w-24 h-0.5 bg-white mx-auto mb-12" />

            {/* Learn More - Scroll to steps section */}
            <div className="max-w-xl mx-auto">
              <div
                id="learn-more"
                role="button"
                tabIndex={0}
                onClick={scrollToSteps}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') scrollToSteps();
                }}
                className="bg-white/10 backdrop-blur-md rounded-lg px-6 py-4 border border-white/20 shadow-xl ring-1 ring-white/10 cursor-pointer hover:bg-white/20 transition-all duration-300 w-full"
                aria-label="Learn more about how it works"
              >
                <div className="flex items-center justify-between gap-6">
                  <div className="text-left">
                    <div className="text-white/90 text-xs uppercase tracking-widest mb-2">
                      LEARN MORE
                    </div>
                    <div className="text-white/70 text-lg">
                      See how it works
                    </div>
                  </div>
                  <MessageCircle className="h-6 w-6 text-white/80 flex-shrink-0" aria-hidden="true" />
                </div>
              </div>
            </div>
          </div>
        </section>

        <div id="steps-section">
          {/* Golden Thread Timeline - horizontal 6-step timeline with connecting line */}
          <section className="py-20 bg-navy-50/50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-16">
                <p className="text-gold-600 font-medium tracking-wide uppercase text-sm mb-3">
                  SIMPLE & TRANSPARENT
                </p>
                <h2 className="font-display text-3xl md:text-4xl text-gold-700 tracking-tight font-semibold">
                  Golden Thread Timeline
                </h2>
              </div>

              {/* Desktop: Horizontal layout (lg and up) */}
              <div className="hidden lg:block relative">
                {/* Horizontal connecting line */}
                <div
                  className="absolute left-0 right-0 top-[2.9375rem] h-0.5 bg-gold-500/80 z-0"
                  aria-hidden="true"
                />
                <div className="grid grid-cols-6 gap-6 relative">
                  {goldenThreadSteps.map((step, index) => (
                    <div key={step.title} className="relative flex flex-col items-center text-center">
                      <div className="w-24 h-24 rounded-full bg-navy-50 flex items-center justify-center font-bold text-gold-600 text-8xl relative z-10 mb-4">
                        {index + 1}
                      </div>
                      <h3 className="font-display text-lg font-semibold text-navy-800 tracking-tight mb-2">
                        {step.title}
                      </h3>
                      <p className="text-navy-600 text-sm leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Mobile/Tablet: Vertical layout with line on left */}
              <div className="lg:hidden relative">
                <div className="max-w-2xl mx-auto">
                  {goldenThreadSteps.map((step, index) => (
                    <div key={step.title} className="relative flex gap-6">
                      {/* Left side: Number + Vertical Line */}
                      <div className="flex flex-col items-center">
                        {/* Number circle */}
                        <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-navy-50 flex items-center justify-center font-bold text-gold-600 text-5xl sm:text-6xl relative z-10 flex-shrink-0">
                          {index + 1}
                        </div>
                        {/* Vertical connecting line (not on last item) */}
                        {index < goldenThreadSteps.length - 1 && (
                          <div className="w-0.5 flex-1 bg-gold-500/80 min-h-[2rem]" aria-hidden="true" />
                        )}
                      </div>
                      {/* Right side: Text content */}
                      <div className="flex-1 pb-8">
                        <h3 className="font-display text-lg sm:text-xl font-semibold text-navy-800 tracking-tight mb-2 pt-3 sm:pt-4">
                          {step.title}
                        </h3>
                        <p className="text-navy-600 text-sm sm:text-base leading-relaxed">
                          {step.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        </div>

        {/* Trust Section */}
        <section className="py-20 bg-navy-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <div className="w-16 h-0.5 bg-gold-500 mx-auto mb-6" />
              <h2 className="font-display text-3xl text-white tracking-tight italic mb-4">
                Why Trust BESPOKIAN?
              </h2>
              <p className="text-navy-200 max-w-2xl mx-auto">
                We're not just another pharmacy. We're your partner in affordable, accessible healthcare.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {trustBadges.map((badge) => (
                <div key={badge.label} className="bg-navy-700/50 rounded-xl p-6 text-center border border-navy-600">
                  <div className="w-12 h-12 bg-gold-500/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <badge.icon className="h-6 w-6 text-gold-400" />
                  </div>
                  <h3 className="text-white font-semibold mb-1">{badge.label}</h3>
                  <p className="text-navy-300 text-sm">{badge.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Teaser */}
        <section className="py-20 bg-sage-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white rounded-2xl shadow-card p-8 md:p-12 text-center">
              <div className="w-16 h-0.5 bg-gold-500 mx-auto mb-6" />
              <h2 className="font-display text-3xl text-navy-800 tracking-tight italic mb-4">
                Have Questions?
              </h2>
              <p className="text-navy-600 mb-8 max-w-xl mx-auto">
                Our team is here to help. Check out our frequently asked questions or talk to a pharmacist directly.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/faq">
                  <button className="px-6 py-3 font-semibold text-navy-800 bg-gold-500 hover:bg-gold-400 rounded-lg transition-all hover:shadow-glow-gold inline-flex items-center justify-center gap-2">
                    View FAQ <ArrowRight className="h-4 w-4" />
                  </button>
                </Link>
                <a href="tel:1-877-237-7679">
                  <button className="px-6 py-3 font-medium text-navy-700 border border-navy-200 hover:bg-navy-50 rounded-lg transition-colors inline-flex items-center justify-center gap-2">
                    <Phone className="h-4 w-4" />
                    Talk to a Pharmacist
                  </button>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="w-16 h-0.5 bg-gold-500 mx-auto mb-6" />
            <h2 className="font-display text-3xl text-navy-800 tracking-tight italic mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-xl text-navy-600 mb-8">
              Start saving money on your prescriptions.
            </p>
            <Link to="/login">
              <button className="px-8 py-4 text-lg font-semibold text-navy-800 bg-gold-500 hover:bg-gold-400 rounded-lg shadow-lg transition-all hover:shadow-glow-gold inline-flex items-center justify-center gap-2">
                Create Free Account <ArrowRight className="h-5 w-5" />
              </button>
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
